import {
  CheckableSelectItem,
  DropdownSelect,
  FlexBox,
  Icon,
  Tooltip,
} from '@dixa/lib-shared-habla';
import {
  AiDraftInitiator,
  AiDraftType,
} from 'lib-knowledge-management-common/graphql';
import { usePaginatedCollections } from 'lib-knowledge-management-common/hooks/usePaginatedCollections';
import { ItemType } from 'lib-knowledge-utils/components/itemType';

import { useShowInternalOnlyCollections } from 'hooks/useShowInternalOnlyCollections';

import {
  SuggestionAudience,
  UseSuggestionsFilters,
} from '../hooks/useSuggestionsFilters';

import s from './suggestionsFilters.module.scss';

const typeItems: ItemType[] = [
  { id: AiDraftType.NewArticle, name: 'New article' },
  { id: AiDraftType.ArticleUpdate, name: 'Article update' },
];

const initiatorItems: ItemType[] = [
  {
    id: AiDraftInitiator.Ai,
    name: 'AI',
    description: 'Gaps the AI found on its own',
  },
  {
    id: AiDraftInitiator.Agent,
    name: 'Agents',
    description: 'Drafts an agent asked for from a conversation',
  },
];

const audienceItems: ItemType[] = [
  {
    id: SuggestionAudience.Internal,
    name: 'Internal only',
    description: 'Drafts for internal only collections, written for agents',
  },
  {
    id: SuggestionAudience.Customers,
    name: 'Customers',
    description: 'Drafts for collections customers can see',
  },
];

function toggled<T>(values: readonly T[], value: T, checked: boolean): T[] {
  return checked
    ? [...values.filter((v) => v !== value), value]
    : values.filter((v) => v !== value);
}

/** The filters for the suggestions list, as the articles list lays them out. */
export function SuggestionsFilters({
  filters,
}: {
  filters: UseSuggestionsFilters;
}): JSX.Element {
  const { selection, setCollectionIds, setTypes, setInitiators, setAudiences } =
    filters;
  const showInternalOnlyCollections = useShowInternalOnlyCollections();
  const { data, hasMore, fetchNextPage } = usePaginatedCollections();

  const collectionItems: ItemType[] = (data?.collections.results ?? []).map(
    (collection) => ({
      id: collection.id,
      name: collection.internalOnly ? (
        <FlexBox as="span" alignItems="center" gap="100">
          {collection.title}
          <Tooltip content="Internal only" wrapProps={{ className: s.lock }}>
            <Icon name="lock" size={16} aria-label="Internal only" />
          </Tooltip>
        </FlexBox>
      ) : (
        collection.title
      ),
    }),
  );

  return (
    <FlexBox flexWrap="wrap" gap="200">
      <DropdownSelect
        fixed
        dropdownProps={{ className: s.panel }}
        buttonProps={{ label: selection.collectionIds.length || undefined }}
        itemComponent={CheckableSelectItem}
        items={collectionItems}
        keepOpen
        noArrow={false}
        onChange={({ id }, checked) =>
          setCollectionIds(
            toggled(selection.collectionIds, id.toString(), checked),
          )
        }
        onClear={() => setCollectionIds([])}
        selectedItems={selection.collectionIds}
        pagination={{
          infinite: true,
          nextEnable: hasMore,
          previousEnable: false,
          onNext: fetchNextPage,
        }}
      >
        Collections
      </DropdownSelect>

      <DropdownSelect
        fixed
        dropdownProps={{ className: s.panel }}
        buttonProps={{ label: selection.types.length || undefined }}
        itemComponent={CheckableSelectItem}
        items={typeItems}
        keepOpen
        noArrow={false}
        onChange={({ id }, checked) =>
          setTypes(toggled(selection.types, id as AiDraftType, checked))
        }
        onClear={() => setTypes([])}
        selectedItems={selection.types}
      >
        Type
      </DropdownSelect>

      <DropdownSelect
        fixed
        dropdownProps={{ className: s.panel }}
        buttonProps={{ label: selection.initiators.length || undefined }}
        itemComponent={CheckableSelectItem}
        items={initiatorItems}
        keepOpen
        noArrow={false}
        onChange={({ id }, checked) =>
          setInitiators(
            toggled(selection.initiators, id as AiDraftInitiator, checked),
          )
        }
        onClear={() => setInitiators([])}
        selectedItems={selection.initiators}
      >
        Initiated by
      </DropdownSelect>

      {showInternalOnlyCollections && (
        <DropdownSelect
          fixed
          dropdownProps={{ className: s.panel }}
          buttonProps={{ label: selection.audiences.length || undefined }}
          itemComponent={CheckableSelectItem}
          items={audienceItems}
          keepOpen
          noArrow={false}
          onChange={({ id }, checked) =>
            setAudiences(
              toggled(selection.audiences, id as SuggestionAudience, checked),
            )
          }
          onClear={() => setAudiences([])}
          selectedItems={selection.audiences}
        >
          Audience
        </DropdownSelect>
      )}
    </FlexBox>
  );
}
